import { useSearchParams } from 'react-router-dom';
import { useSelfregSessionEnrollment } from '@routes/selfreg/hooks/useSelfregSessionEnrollment';
import { useEnrollmentError } from '@hooks/feedback/useEnrollmentError';
import { useEnrollmentFeedback } from '@hooks/feedback/useEnrollmentFeedback';
import { FC, useEffect, useRef } from 'react';
import { FaIcon, StatusModal } from '@fe-learner-common';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { TW_ROOT } from '@utils/constants';
import { useIntl } from 'react-intl';

export const AutoEnrollment: FC<{
  sessionId: string;
  courseName?: string | null;
}> = ({ sessionId, courseName }) => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const shouldAutoEnroll = searchParams.get('op') === 'enroll';
  const hasInvokedEnrollment = useRef(false);

  const {
    mutate: enroll,
    data: enrollmentData,
    isPending,
    error,
  } = useSelfregSessionEnrollment({
    sessionId,
  });

  useEnrollmentError(error);
  useEnrollmentFeedback({
    enrollmentStatus: enrollmentData?.status,
    overrideStartCourseLink:
      enrollmentData?.session.selfEnrollmentParticipationUrl || undefined,
    sessionId,
  });

  useEffect(() => {
    if (shouldAutoEnroll && !hasInvokedEnrollment.current) {
      hasInvokedEnrollment.current = true;
      /* setTimeout due to a bug in react-query, causing mutation to trigger twice when using StrictMode.
      https://github.com/TanStack/query/issues/5341 */
      setTimeout(() => enroll(), 200);
    }
  }, [shouldAutoEnroll]);

  if (!shouldAutoEnroll) {
    return null;
  }

  return (
    <StatusModal
      className="z-10"
      open={isPending}
      container={document.getElementById(TW_ROOT) as HTMLElement}
    >
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-[100px]">
        <FaIcon
          height={40}
          width={40}
          icon={faSpinner}
          className="animate-spin text-neutral-500"
        />
        <h2 className="pb-2 pt-4 text-headline-md">
          {intl.formatMessage({
            id: 'catalog.course.action.enroll.pending.title',
          })}
        </h2>
        {courseName && (
          <p className="text-center text-body-md text-neutral-500">
            {intl.formatMessage(
              {
                id: 'catalog.course.action.enroll.pending.message',
              },
              { courseName },
            )}
          </p>
        )}
      </div>
    </StatusModal>
  );
};
